import Multipoint from '@arcgis/core/geometry/Multipoint';
import Point from '@arcgis/core/geometry/Point';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import MapView from '@arcgis/core/views/MapView';
import * as esriGeometryEngine from '@arcgis/core/geometry/geometryEngine';
import Polyline from '@arcgis/core/geometry/Polyline';
import Graphic from '@arcgis/core/Graphic';
import { MapViewInputTask } from 'cd-eft/components/Design/BreaklineTools/framework/MapViewInputTask';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';

interface IOptions {
  allowSelfIntersecting: boolean;
}

export class DrawPolylineInputTask extends MapViewInputTask<Polyline> {
  //setup state vars
  private options: IOptions;
  private pointerMovePoint?: Point;
  private inputPoints: Point[] = [];
  private redoStack: Point[] = [];

  private feedbackLayer = new GraphicsLayer({ title: 'feedbackLayer', listMode: 'show' });
  private resultLayer = new GraphicsLayer({ title: 'resultLayer', listMode: 'show' });

  constructor(mapView: MapView, options?: IOptions) {
    super(mapView);
    this.options = {
      allowSelfIntersecting: true,
    };
    if (options && options.allowSelfIntersecting !== undefined) {
      this.options.allowSelfIntersecting = options.allowSelfIntersecting;
    }
  }

  //overridable lifecycle methods
  protected onSetup() {
    super.onSetup();

    this.pointerMovePoint = undefined;
    this.inputPoints = [];
    this.redoStack = [];

    this.cursor = 'crosshair';
    this.mapView.map.add(this.feedbackLayer);
    this.mapView.map.add(this.resultLayer);
  }
  protected onCleanup() {
    super.onCleanup();

    this.mapView.map.remove(this.feedbackLayer);
    this.mapView.map.remove(this.resultLayer);
    this.feedbackLayer.removeAll();
    this.resultLayer.removeAll();

    this.pointerMovePoint = undefined;
    this.inputPoints = [];
    this.redoStack = [];
  }
  protected onPointerMove(e: __esri.ViewPointerMoveEvent): void {
    super.onPointerMove(e);

    this.pointerMovePoint = this.mapView.toMap(e);
    this.updateFeedbackLayer();
  }
  protected onImmediateClick(e: __esri.ViewImmediateClickEvent) {
    super.onImmediateClick(e);

    const nextPoint = e.mapPoint.clone();

    //validate input
    const valid = this.validateNextPoint(nextPoint);
    if (!valid) {
      // ignore user input
      return; //short circuit
    }

    //if valid, add the point
    this.inputPoints.push(nextPoint);

    // clear the redoStack, it is now invalidated
    this.redoStack.splice(0, this.redoStack.length);

    this.updateResultLayer();
    this.updateFeedbackLayer();
  }
  protected onImmediateDoubleClick(e: __esri.ViewImmediateDoubleClickEvent) {
    super.onImmediateDoubleClick(e);

    e.stopPropagation();

    const polyline = this.constructPolylineResult();
    if (polyline) {
      this.completeNow(polyline);
    } else {
      // this.cancelNow();
    }
  }
  protected onKeyDown(e: __esri.ViewKeyDownEvent) {
    super.onKeyDown(e);

    if (e.key === 'Enter') {
      e.stopPropagation();
      const polyline = this.constructPolylineResult();
      if (polyline) {
        this.completeNow(polyline);
      } else {
        // this.cancelNow();
      }
    }

    if (e.key === 'z' || e.key === 'Z') {
      // undo last digitized point
      e.stopPropagation();
      if (this.inputPoints.length > 0) {
        this.redoStack.push(this.inputPoints.pop()!);
        this.updateResultLayer();
        this.updateFeedbackLayer();
      }
      return;
    }
    if (e.key === 'r' || e.key === 'R') {
      // redo last digitized point
      e.stopPropagation();
      if (this.redoStack.length > 0) {
        this.inputPoints.push(this.redoStack.pop()!);
        this.updateResultLayer();
        this.updateFeedbackLayer();
      }
      return;
    }
  }

  private constructPolylineResult(): Polyline | undefined {
    // console.log('this.inputPoints.length', this.inputPoints.length);
    //reconstruct the line graphic
    if (this.inputPoints.length < 2) return undefined;

    const path: Point[] = [];
    for (const point of this.inputPoints) {
      path.push(point.clone());
    }
    const polyline = new Polyline({ spatialReference: this.mapView.spatialReference.clone() });
    polyline.addPath(path);

    return polyline;
  }

  private validateNextPoint(nextPoint: Point): boolean {
    // detect non-eligible point

    if (!this.options.allowSelfIntersecting) {
      //// this logic doesn't detect self intersection for some reason, esri bug?
      // console.log('check self-intersecting');
      // const polyline = this.constructPolylineResult();
      // if (polyline) {
      //   polyline.insertPoint(0, polyline.paths[0].length, nextPoint);
      //   const simple = esriGeometryEngine.isSimple(polyline);
      //   console.log('simple', simple);
      // }
    }

    return true;
  }

  private updateFeedbackLayer(): void {
    //clear the layer
    this.feedbackLayer.removeAll();
    if (!this.pointerMovePoint) return;
    if (this.inputPoints.length < 1) return;

    const point1 = this.inputPoints[this.inputPoints.length - 1];
    const point2 = this.pointerMovePoint;
    const path = [point1, point2];
    const polyline = new Polyline({ spatialReference: this.mapView.spatialReference.clone() });
    polyline.addPath(path);

    const symbol = new SimpleLineSymbol();
    const graphic = new Graphic({ geometry: polyline, symbol: symbol });
    this.feedbackLayer.add(graphic);
  }

  private updateResultLayer() {
    //clear the layer
    this.resultLayer.removeAll();

    // //reconstruct the line graphic
    // if (this.inputPoints.length < 2) return;

    // const path: Point[] = [];
    // for (const point of this.inputPoints) {
    //   path.push(point.clone());
    // }
    // const polyline = new Polyline({ spatialReference: this.mapView.spatialReference.clone() });
    // polyline.addPath(path);
    const polyline = this.constructPolylineResult();
    if (!polyline) return;

    const symbol = new SimpleLineSymbol();
    const graphic = new Graphic({ geometry: polyline, symbol: symbol });

    this.resultLayer.add(graphic);
  }
}
